import Modal from 'react-modal';
import { useState, useEffect } from 'react';
import { renderStars } from "../utils/renderStars"; // Import the function
import { Rating, Typography } from "@material-tailwind/react";
import { useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { formatDistanceToNow } from "date-fns";

import { StarIcon, CircleUser } from 'lucide-react';

import StickyNavbar from "../components/StickyNavbar";
import ReviewDrawer from "../components/ReviewDrawer";
import CustomLoader from '../components/CustomLoader';
import LoadingSkeleton from '../components/LoadingSkeleton';
import config from '../utils/config';

import { rateTool, fetchTool, fetchCategory, recordToolView, toolLinkClicked } from "../api/api";

const ToolDetails = () => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const handleRating = (star) => setRating(star);

  const location = useLocation();
  const { toolcard } = location.state || {}; // Get passed value
  const currentToolId = toolcard?._id;

  const [tool, setTool] = useState(null);
  const [rateError, setRateError] = useState(false);
  const [categoryName, setCategoryName] = useState('Loading...');
  const navigate = useNavigate();

  // Loading Bars
  const [loadingRate, setLoadingRate] = useState(false);
  const [loading, setLoading] = useState(true); // Start with loading=true

  const [userRating, setUserRating] = useState(0);

  const token = Cookies.get('authToken');

  const handleSubmitReview = async () => {
    setLoadingRate(true); // Show spinner

    try {
      console.log('Rating:', userRating);
      console.log('Comment:', comment);

      if(userRating < 1 || comment.trim() === '') { // Fixed comparison
        setRateError(true);
      } else {
        const results = await rateTool(tool._id, userRating, comment, token);

        setUserRating(0);
        setComment('');

        const updateTool = await fetchTool(tool._id);
        console.log(updateTool);

        setTool(updateTool.tool);

        setRateError(false);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setLoadingRate(false); // Hide spinner
    }
  };

  const getCatgName = async (categoryId) => {
    try {
      const results = await fetchCategory(categoryId);
      setCategoryName(results.category.name);
    } catch (error) {
      console.error('Error fetching category:', error);
    }
  };

  const goToCatg = async () => {
    try {
      if (tool) {
        navigate("/catgtools", { state: { categoryId: tool.category, categoryName: categoryName } });
      }
    } catch (error) {
      console.error('Error navigating to category:', error);
    }
  };

  const fetchData = async () => {
    if (!toolcard || !toolcard._id) {
      console.error('No tool data available');
      setLoading(false);
      return;
    }
    
    try {
      const updateTool = await fetchTool(toolcard._id);
      setTool(updateTool.tool);
      
      // Only fetch category after tool is loaded
      if (updateTool.tool && updateTool.tool.category) {
        getCatgName(updateTool.tool.category);
      }
      
      if (updateTool.tool && updateTool.tool._id) {
        await recordToolView(updateTool.tool._id, token);
      }
    } catch (error) {
      console.error('Error fetching tool data:', error);
    } finally {
      setLoading(false);
    }
  };

  const linkClicked = async () => {
    try {
      if (tool && tool._id) {
        const clickResult = await toolLinkClicked(tool._id, token);
      }
    } catch (error) {
      console.error('Error recording link click:', error);
    }
  };

  useEffect(() => {
    if (currentToolId) {
      // Reset component state
      setTool(null);
      setCategoryName('Loading...');
      setUserRating(0);
      setComment('');
      setRateError(false);
      
      // Start loading new data
      setLoading(true);
      fetchData();
      
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';
    }
  }, [currentToolId]);

  const openToolLink = () => {
    if (tool && tool.website) {
      linkClicked();
      window.open(tool.website, "_blank");
    }
  };

  // Show loading spinner while data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-6">
        <StickyNavbar />
        <div className="pt-20">
          <CustomLoader />
          {/*<p className="mt-4 text-gray-600">Loading tool details...</p>*/}
        </div>
      </div>
    );
  }

  // Show message if no tool data is available
  if (!tool) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-6">
        <StickyNavbar />
        <div className="pt-20">
          <p className="text-xl text-gray-600">Tool not found or data unavailable</p>
          <button 
            className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            onClick={() => navigate('/')}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-6">
      {/* Header Section with improved layout */}
      <StickyNavbar />
      <div className="w-full max-w-4xl pt-20">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="md:flex">
            {/* Left side - Logo */}
            <div className="md:w-1/3 bg-gradient-to-br from-gray-50 to-gray-100 p-8 flex items-center justify-center">
              <div className="text-center">
                <img
                  src={`${config.IMG_URL}/images/tools/${tool.logo}`}
                  alt={tool.name}
                  className="h-auto w-auto mx-auto"
                />
                <div className="mt-4 text-xl font-bold">AI Tool</div>
                <div className="text-sm text-gray-500">Listed on Tuly</div>
              </div>
            </div>
            
            {/* Right side - Product info */}
            <div className="md:w-2/3 p-8">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{tool.name}</h1>
                  <p className="mt-2 text-lg text-gray-600 cursor-pointer" onClick={goToCatg}>{categoryName}</p>
                  <p className="text-gray-600">{tool.pricing}</p>
                  
                  <div className="mt-4 flex items-center">
                    {renderStars(tool.averageRating || 0)} 
                    <span className="ml-2 text-sm text-gray-500">Based on {tool.ratings?.length || 0} ratings</span>
                  </div>
                </div>
                
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-lg transition-colors" onClick={openToolLink}>
                  Try it!
                </button>
              </div>
              
              <div className="mt-6">
                <h2 className="text-xl font-semibold text-gray-800">Description:</h2>
                <p className="mt-2 text-gray-600">
                  {tool.description}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Features Section - Better spacing and visual hierarchy */}
        <div className="mt-8 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-800">Features</h2>
          <ul className="mt-4 grid md:grid-cols-2 gap-x-6 gap-y-3">
            {tool.features?.map((feature, index) => (
              <li key={index} className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-800">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="ml-3 text-gray-600">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
         
        {/* Reviews Section - Completely redesigned for better UX */}
        <div className="mt-8 bg-white rounded-xl shadow-md p-8">

          <div className="flex justify-center items-center">
            {loadingRate && <CustomLoader />}
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <h2 className="text-2xl font-bold text-gray-800">Users' reviews</h2>
            <span className="text-gray-500 text-sm mt-1 md:mt-0">({tool.ratings?.length || 0} reviews)</span>
          </div>
          
          <div className="mt-6 space-y-6">
            {tool.ratings?.map((review, index) => (
              <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                      <CircleUser />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">{review.comment}</h3>
                      <p className="text-sm text-gray-500">{formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}</p>
                    </div>
                    <div className="mt-1">
                      {renderStars(review.stars)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Write a review section */}
          <div className="mt-8 pt-6 border-t border-gray-200">            

            <h3 className="text-lg font-medium text-gray-900">Share your experience</h3>

            {/* Rate form validation message*/}
            {rateError && (
              <div id="toast-warning" className="flex items-center w-full border border-red-500 max-w-xs p-4 text-gray-500 bg-red rounded-lg shadow-sm dark:text-gray-400 dark:bg-red-800" role="alert">
                <div className="inline-flex items-center justify-center shrink-0 w-8 h-8 text-orange-500 bg-orange-100 rounded-lg dark:bg-orange-700 dark:text-orange-200">
                  <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V6a1 1 0 0 1 2 0v5Z"/>
                  </svg>
                  <span className="sr-only">Warning icon</span>
                </div>
                <div className="ms-3 text-sm font-normal">Stars and Review then hit it!</div>
                <button 
                  type="button" 
                  className="ms-auto -mx-1.5 -my-1.5 bg-white text-black hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700" 
                  onClick={() => setRateError(false)}
                >
                  <span className="sr-only">Close</span>
                  <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                  </svg>
                </button>
              </div>
            )}

            <div className="mt-4">
              <div className="flex items-center">
                <p className="mr-4 font-medium"> </p>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      className={`w-8 h-8 cursor-pointer ${
                        star <= userRating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                      onClick={() => setUserRating(star)}
                    />
                  ))}
                </div>
              </div>
              
              <div className="mt-4">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Write your review..."
                ></textarea>
                <div className="mt-3 text-right">
                  <button className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors" onClick={handleSubmitReview}>
                    Submit Review
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolDetails;